import { ReactNode, useEffect, useRef, useState } from 'react';
import { CSSTransition } from 'react-transition-group';

import CloseIconSvg from 'public/icons/close.svg';

import { Button } from '../_Button/Button';

import styles from './Modal.module.css';

type ModalProps = {
	children: ReactNode;
	classes?: string;
	modalState: boolean;
	setModalState: (value: boolean) => void;
	afterClose?: () => void;
};

export function Modal(props: ModalProps): JSX.Element {
	const modalRef = useRef<HTMLElement>(null);
	const [internalModalState, setInternalModalState] = useState(props.modalState);

	useEffect(() => {
		if (internalModalState === props.modalState) {
			return;
		}

		setInternalModalState(props.modalState);
	}, [props.modalState]);

	useEffect(() => {
		if (internalModalState) {
			// Lock scroll
			const scrollY = window.scrollY;
			document.body.style.position = 'fixed';
			document.body.style.top = `-${scrollY}px`;
			document.body.style.width = '100%';
		} else {
			// Unlock scroll
			const scrollY = document.body.style.top;
			document.body.style.position = '';
			document.body.style.top = '';
			document.body.style.width = '';
			window.scrollTo(0, parseInt(scrollY || '0') * -1);
		}

		// Clean up in case modal unmounts while open
		return () => {
			document.body.style.position = '';
			document.body.style.top = '';
			document.body.style.width = '';
		};
	}, [internalModalState]);

	return (
		<CSSTransition
			nodeRef={modalRef}
			in={internalModalState}
			timeout={300}
			classNames={{ ...styles }}
			onExited={() => {
				props.setModalState(internalModalState);
				props.afterClose?.();
			}}
			unmountOnExit
		>
			<aside className={styles.modal} ref={modalRef}>
				<div className={styles.backdrop} onClick={() => setInternalModalState(false)} />

				<div className={`${styles.outer} ${props.classes} maxWidth rounded`}>
					<Button title="Close Icon" className={styles.close} onClick={() => setInternalModalState(false)}>
						<CloseIconSvg className={styles.icon} />
					</Button>

					<div className={styles.inner}>{props.children}</div>
				</div>
			</aside>
		</CSSTransition>
	);
}
